import {
  type ChangeEvent,
  type FormEvent,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FounderBrandMark } from "../../components/founder/FounderBrandMark";
import { PasswordInputWithToggle } from "../../components/PasswordInputWithToggle";
import { useFounderAuth } from "../../context/FounderAuthContext";
import { FOUNDER_HOME_PATH } from "../../founder/founderNavigation";
import {
  normalizePublisherLoginInput,
  resolveAuthErrorMessage,
  validatePublisherLoginInput,
} from "../../services/publisherAuth";

type LogInFormValues = {
  email: string;
  password: string;
};

type FounderLogInLocationState = {
  from?: string;
};

const initialValues: LogInFormValues = {
  email: "",
  password: "",
};

export function FounderLogInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeMfaLogin } = useFounderAuth();
  const locationState = (location.state as FounderLogInLocationState | null) ?? null;
  const [values, setValues] = useState<LogInFormValues>(initialValues);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }

    setFormError("");
    const errors = validatePublisherLoginInput(values);
    if (errors.length > 0) {
      setValidationErrors([errors[0]]);
      return;
    }

    setValidationErrors([]);
    setSubmitting(true);

    try {
      const normalized = normalizePublisherLoginInput(values);
      const result = await login(normalized.email, normalized.password);

      if (!result.completed) {
        setMfaChallengeId(result.challengeId);
        return;
      }

      navigate(locationState?.from ?? FOUNDER_HOME_PATH, { replace: true });
    } catch (error: unknown) {
      setValidationErrors([]);
      setFormError(resolveAuthErrorMessage(error, "login"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!mfaChallengeId || submitting) {
      return;
    }

    setFormError("");
    setSubmitting(true);

    try {
      await completeMfaLogin(mfaChallengeId, mfaCode.trim());
      navigate(locationState?.from ?? FOUNDER_HOME_PATH, { replace: true });
    } catch (error: unknown) {
      setFormError(resolveAuthErrorMessage(error, "login"));
    } finally {
      setSubmitting(false);
    }
  }

  const errorMessage = formError || validationErrors[0] || "";

  return (
    <div className="oac-founder-login">
      <article aria-label="OpenAppCart Founder" className="oac-founder-login__card">
        <header className="oac-founder-login__header">
          <FounderBrandMark asLink={false} subIdentity="Founder" subIdentityInline />
        </header>

        {!mfaChallengeId ? (
          <form className="oac-founder-login__form" onSubmit={handleSubmit}>
            <div className="oac-founder-login__field">
              <label htmlFor="founder-log-in-email">Email</label>
              <input
                autoComplete="email"
                id="founder-log-in-email"
                name="email"
                onChange={handleChange}
                required
                type="email"
                value={values.email}
              />
            </div>

            <div className="oac-founder-login__field">
              <label htmlFor="founder-log-in-password">Password</label>
              <PasswordInputWithToggle
                autoComplete="current-password"
                id="founder-log-in-password"
                name="password"
                onChange={handleChange}
                required
                value={values.password}
              />
            </div>

            {errorMessage ? (
              <p aria-live="polite" className="oac-founder-login__error">
                {errorMessage}
              </p>
            ) : null}

            <button
              className="oac-founder-login__submit"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Please wait..." : "Sign in"}
            </button>
          </form>
        ) : (
          <form className="oac-founder-login__form" onSubmit={handleMfaSubmit}>
            <p className="oac-founder-login__helper">
              Enter the 6-digit code from your authenticator app.
            </p>
            <div className="oac-founder-login__field">
              <label htmlFor="founder-log-in-mfa">Verification code</label>
              <input
                autoComplete="one-time-code"
                id="founder-log-in-mfa"
                inputMode="numeric"
                onChange={(e) => setMfaCode(e.target.value)}
                required
                value={mfaCode}
              />
            </div>
            {errorMessage ? (
              <p aria-live="polite" className="oac-founder-login__error">
                {errorMessage}
              </p>
            ) : null}
            <button
              className="oac-founder-login__submit"
              disabled={submitting || !mfaCode.trim()}
              type="submit"
            >
              {submitting ? "Please wait..." : "Verify and sign in"}
            </button>
          </form>
        )}
      </article>
    </div>
  );
}
